import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { TrendingUp, Trophy, Target, Percent, Crown, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import BottomTabBar from '../components/BottomTabBar';
import { useAuth } from '../providers/AuthProvider';
import { apiMethods } from '../services/api';
import { safeDecodeURIComponent, getInitials, formatDisplayName, formatChartDate, formatTooltipDateTime, debugTimezone } from '../utils/textUtils';

const MyStatsPage = () => {
  const { status, uid, name, avatar, error: authError, retry } = useAuth();
  const [overview, setOverview] = useState(null);
  const [eloProgress, setEloProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (status === 'ready' && uid) {
      loadData();
    }
  }, [status, uid]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [overviewRes, progressRes] = await Promise.all([
        apiMethods.getPlayerOverview(uid),
        apiMethods.getPlayerEloProgress(uid, {
          from: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(), // 90 days ago
        }),
      ]);

      setOverview(overviewRes.data);
      setEloProgress(progressRes.data);
      
      // Debug log เพื่อดูข้อมูล elo progress และ timezone
      console.log('Elo Progress data:', progressRes.data);
      console.log('Type of progressRes.data:', typeof progressRes.data);
      console.log('Is array:', Array.isArray(progressRes.data));
      if (Array.isArray(progressRes.data)) {
        console.log('Elo history length:', progressRes.data.length);
        console.log('First history point:', progressRes.data[0]);
        if (progressRes.data[0]) {
          console.log('Timezone debug for first point:', debugTimezone(progressRes.data[0].changed_at));
        }
      } else if (progressRes.data?.history) {
        console.log('Elo history length:', progressRes.data.history.length);
        console.log('First history point:', progressRes.data.history[0]);
        if (progressRes.data.history[0]) {
          console.log('Timezone debug for first point:', debugTimezone(progressRes.data.history[0].changed_at));
        }
      }
    } catch (err) {
      console.error('Error loading stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (status === 'authenticating') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-100 via-blue-50 to-white flex items-center justify-center font-prompt">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังเข้าสู่ระบบ...</p>
        </div>
      </div>
    );
  }

  if (status === 'error' && authError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-100 via-blue-50 to-white flex items-center justify-center p-4 font-prompt">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-800 text-sm">{authError}</p>
          </div>
          <Button onClick={retry} className="bg-green-600 hover:bg-green-700">
            ลองใหม่
          </Button>
        </div>
      </div>
    );
  }

  if (status !== 'ready') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-100 via-blue-50 to-white flex items-center justify-center font-prompt">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  const formatTierLabel = (tierLabel) => {
    if (!tierLabel) return '';
    // Extract tier letter/name (assuming format like "F Tier", "S Tier", etc.)
    const tier = tierLabel.replace(' Tier', '').trim();
    return `เบลดเดอร์ แรงค์ ${tier}`;
  };

  const getTierStyle = (tierLabel) => {
    if (!tierLabel) return 'bg-gray-100 text-gray-800';
    
    const tier = tierLabel.replace(' Tier', '').trim().toUpperCase();
    
    // ไล่จากสีอ่อนไปสีเข้ม (X คือเก่งที่สุด)
    const tierStyles = {
      'F': 'bg-slate-100 text-slate-600 border-slate-200',     // สีอ่อนที่สุด
      'E': 'bg-red-100 text-red-700 border-red-200',          
      'D': 'bg-orange-100 text-orange-700 border-orange-200', 
      'C': 'bg-yellow-100 text-yellow-700 border-yellow-200', 
      'B': 'bg-green-100 text-green-700 border-green-200',    
      'A': 'bg-blue-100 text-blue-700 border-blue-200',      
      'X': 'bg-purple-600 text-white border-purple-700 shadow-lg', // สีเข้มที่สุด/เก่งที่สุด
    };
    
    return tierStyles[tier] || 'bg-gray-100 text-gray-800';
  };

  const formatChartData = (history) => {
    if (!history || !Array.isArray(history)) return [];
    
    // เรียงตามวันที่เก่าไปใหม่
    const sortedHistory = [...history].sort((a, b) => new Date(a.changed_at) - new Date(b.changed_at));
    
    return sortedHistory.map((point, index) => ({
      date: formatChartDate(point.changed_at),
      rating: point.new_rating,
      fullDate: formatTooltipDateTime(point.changed_at),
      index: index + 1
    }));
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">{data.fullDate}</p>
          <p className="text-lg font-bold text-blue-600">
            Elo: {payload[0].value}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-100 via-blue-50 to-white pb-20 font-prompt">
      <div className="max-w-md mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">สถิติของฉัน</h1>
          
          {/* Profile Section */}
          <Card className="p-4 bg-gradient-to-r from-blue-500 to-cyan-500 border-0 shadow-lg">
            <div className="flex items-center space-x-4">
              <Avatar className="h-16 w-16 border-2 border-white/30">
                <AvatarImage src={overview?.avatar} alt={formatDisplayName(name)} />
                <AvatarFallback className="bg-white/20 text-white font-semibold">{getInitials(name)}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h2 className="text-lg font-semibold text-white">
                    {formatDisplayName(name)}
                  </h2>
                </div>
                {loading ? (
                  <Skeleton className="h-4 w-32 mt-1" />
                ) : overview?.tier_label ? (
                  <Badge className={`mt-2 ${getTierStyle(overview.tier_label)}`}>
                    {formatTierLabel(overview.tier_label)}
                  </Badge>
                ) : (
                  <p className="text-sm text-white/80 mt-1">ไม่มีข้อมูลแรงค์</p>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Card className="p-4">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-600">Elo ปัจจุบัน</span>
            </div>
            {loading ? (
              <Skeleton className="h-8 w-16 mt-2" />
            ) : (
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {overview?.rating || '—'}
              </p>
            )}
          </Card>

          <Card className="p-4">
            <div className="flex items-center space-x-2">
              <Trophy className="h-5 w-5 text-yellow-600" />
              <span className="text-sm font-medium text-gray-600">อันดับโลก</span>
            </div>
            {loading ? (
              <Skeleton className="h-8 w-16 mt-2" />
            ) : (
              <p className="text-2xl font-bold text-gray-900 mt-1">
                #{overview?.global_rank || '—'}
              </p>
            )}
          </Card>

          <Card className="p-4">
            <div className="flex items-center space-x-2">
              <Target className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-gray-600">แมทซ์ทั้งหมด</span>
            </div>
            {loading ? (
              <Skeleton className="h-8 w-16 mt-2" />
            ) : (
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {overview?.rated_matches || '—'}
              </p>
            )}
          </Card>

          <Card className="p-4">
            <div className="flex items-center space-x-2">
              <Percent className="h-5 w-5 text-purple-600" />
              <span className="text-sm font-medium text-gray-600">เปอร์เซ็นไทล์</span>
            </div>
            {loading ? (
              <Skeleton className="h-8 w-16 mt-2" />
            ) : (
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {overview?.percentile ? `${overview.percentile}%` : '—'}
              </p>
            )}
          </Card>
        </div>

        {/* Elo Progress Chart */}
        <Card className="p-4 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">พัฒนาการ Elo</h3>
          {loading ? (
            <Skeleton className="h-48 w-full" />
          ) : (() => {
            // รองรับทั้งกรณีที่ข้อมูลอยู่ใน .history และกรณีที่เป็น array โดยตรง
            const historyData = Array.isArray(eloProgress) ? eloProgress : eloProgress?.history;
            
            return historyData?.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart 
                    data={formatChartData(historyData)}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="date" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#666' }}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#666' }}
                      domain={['dataMin - 50', 'dataMax + 50']}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line 
                      type="monotone" 
                      dataKey="rating" 
                      stroke="#2563eb" 
                      strokeWidth={3}
                      dot={{ fill: '#2563eb', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#2563eb', strokeWidth: 2, fill: '#fff' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <div className="text-4xl mb-2">📊</div>
                  <p className="text-lg font-medium mb-1">ไม่มีข้อมูลประวัติ Elo</p>
                  <p className="text-sm text-gray-400">เล่นแมทซ์ Ranked เพื่อดูกราฟพัฒนาการ</p>
                </div>
              </div>
            );
          })()}
        </Card>

        {/* Peak Elo Highlight */}
        {overview?.elo_max && (
          <Card className="p-4 mb-6 bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200">
            <div className="flex items-center space-x-3">
              <Crown className="h-6 w-6 text-yellow-600" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Elo สูงสุด (Peak Elo)</p>
                <p className="text-xl font-bold text-yellow-900">{overview.elo_max}</p>
              </div>
            </div>
          </Card>
        )}

        {/* CTA Button */}
        <Button 
          onClick={() => navigate('/mymatches')}
          className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white border-0 shadow-lg"
        >
          <span>ไปดูแมทซ์ล่าสุดของคุณ</span>
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>

        {/* Error Display */}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm">{error}</p>
            <Button 
              onClick={loadData} 
              variant="outline" 
              size="sm" 
              className="mt-2"
            >
              ลองใหม่
            </Button>
          </div>
        )}
      </div>

      <BottomTabBar />
    </div>
  );
};

export default MyStatsPage;